import { isSport } from '@pickpro/domain';
import { isLocale } from '@pickpro/i18n';
import { lazy, Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useParams } from 'react-router';
import { changeLocale } from '../shared/i18n/i18n';
import { recoverFromChunkError } from './chunk-reload';
import { publicPagePaths } from './public-pages';
import { useSession } from '../shared/session/use-session';
import { useSessionHint } from '../shared/session/use-session-hint';
import { RootRedirect } from './root-redirect';
import { SessionShellSkeleton } from './session-skeleton';
import { SiteFooter } from './site-footer';
import { SiteHeader } from './site-header';
import { useRouteScroll } from '../shared/navigation/use-route-scroll';
import { usePageMetadata } from '../shared/navigation/use-page-metadata';
import { PageTitleProvider } from '../shared/navigation/page-title';
import { LoadingState } from '../shared/ui/loading-state';
import { freeScans } from '../shared/pricing/plan';

const SignedInShell = lazy(async () => {
  try {
    const module = await import('./signed-in-shell');
    return { default: module.SignedInShell };
  } catch (error) {
    recoverFromChunkError(error);
    throw error;
  }
});

export const LocaleLayout = () => {
  const { user, isPending } = useSession();
  const sessionHint = useSessionHint();
  useRouteScroll();
  const { locale } = useParams();
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation();
  const [pageTitle, setPageTitle] = useState<string | null>(null);
  const supported = isLocale(locale);
  const segments = pathname.split('/');
  const sportSegment = isSport(segments[2]) ? segments[2] : null;
  const pagePath = sportSegment ? segments[3] || 'overview' : segments[2];
  const legalDocument =
    pagePath === 'responsible-gambling'
      ? 'responsible'
      : pagePath === 'terms' || pagePath === 'privacy'
        ? pagePath
        : null;
  const pageTitles: Record<string, string> = {
    'sign-up': t('auth.signUp.title'),
    'sign-in': t('auth.signIn.title'),
    'forgot-password': t('auth.forgot.title'),
    'reset-password': t('auth.reset.title'),
    'verify-email': t('auth.verify.title'),
    access: t('access.title'),
    settings: t('settings.title'),
    admin: t('admin.users.title'),
    matches: t('matches.title'),
    radar: t('radar.title', { sport: sportSegment ? t(`sports.${sportSegment}`) : '' }).trim(),
    'ticket-generator': t('ticket.title'),
    notebook: t('study.title'),
    bankrolls: t('study.bankrolls'),
    bets: t('bets.title'),
    overview: t('nav.overview'),
    search: t('nav.search'),
    competitions: t('nav.competitions'),
    teams: t('nav.teams'),
    players: t('nav.players'),
  };
  const title =
    pageTitle ??
    (legalDocument
      ? t(`legal.${legalDocument}.title`)
      : (pageTitles[pagePath ?? ''] ?? t('landing.meta.title')));
  const description = legalDocument
    ? t(`legal.${legalDocument}.description`)
    : t('landing.meta.description', { freeScans });
  const publicPage = (publicPagePaths as readonly string[]).includes(pagePath ?? '');
  usePageMetadata(
    title === t('app.name') ? title : `${title} | ${t('app.name')}`,
    description,
    !publicPage || pagePath === 'terms' || pagePath === 'privacy',
  );

  useEffect(() => {
    if (supported && i18n.language !== locale) void changeLocale(locale);
  }, [supported, locale, i18n.language]);

  if (!supported) return <RootRedirect />;
  if (i18n.language !== locale) return null;

  const outlet = (
    <PageTitleProvider value={setPageTitle}>
      <Suspense fallback={<LoadingState rows={4} />}>
        <Outlet />
      </Suspense>
    </PageTitleProvider>
  );

  if (!user && isPending && sessionHint) return <SessionShellSkeleton />;

  if (!user)
    return (
      <div
        data-shell="public"
        className="flex min-h-dvh flex-col bg-canvas font-sans text-content antialiased"
      >
        <SiteHeader />
        <main className="mx-auto w-full max-w-6xl flex-1 px-4 sm:px-6">{outlet}</main>
        <SiteFooter />
      </div>
    );

  return (
    <Suspense fallback={<SessionShellSkeleton />}>
      <SignedInShell user={user}>{outlet}</SignedInShell>
    </Suspense>
  );
};
